import mfeConfig from '../mfe.config';
import {
    TAppContextConfig,
    TGatekeeper,
    TGatekeeperContextConfig,
} from './types';
import { IS_BROWSER, LOCALE_TO_MARKET, ROUTES_IDS } from './common/constants';
import { TRouteParams } from '@casumo/fabric-fundamentals';
import { compile } from 'path-to-regexp';

export type TParamsMapper = (params: TRouteParams) => TRouteParams;

const SLUG_MAPPER: Record<string, TParamsMapper> = {
    [ROUTES_IDS.PROMOTION_DETAILS]: params => ({
        promotionSlug: params.promotionSlug ?? params.slug,
    }),

    [ROUTES_IDS.PLAY]: params => ({
        gameSlug: params.gameSlug ?? params.slug,
    }),
    [ROUTES_IDS.PRACTICE]: params => ({
        gameSlug: params.gameSlug ?? params.slug,
    }),
};

const getSSRGatekeeper = (
    gateKeeperContext?: TGatekeeperContextConfig
): TGatekeeper => ({
    messageBus: {
        subscribe: () => {},
        publish: () => {},
    } as any,
    auth: {
        logIn: () => {},
        logOut: () => {},
        isLoggedIn: () => gateKeeperContext?.auth?.isLoggedIn ?? false,
        getAccessToken: () => '',
        getCasumoName: () => gateKeeperContext?.auth?.getCasumoName ?? '',
        getName: () => gateKeeperContext?.auth?.getName ?? '',
        getSessionId: () => gateKeeperContext?.auth?.getSessionId ?? '',
        getPlayerId: () => gateKeeperContext?.auth?.getPlayerId ?? '',
    } as any,
    localisation: {
        marketSlug: gateKeeperContext?.market ?? 'en',
        localeCode: gateKeeperContext?.localeCode ?? 'en',
        systemMarketSlug:
            LOCALE_TO_MARKET[
                gateKeeperContext?.market as keyof typeof LOCALE_TO_MARKET
            ] ?? 'en',
    } as any,
    navigation: {
        navigate: async () => {},
        isActiveRoute: () => false,
        getRoute: (routeId: string, params?: TRouteParams) => {
            const route =
                gateKeeperContext?.operationalNavigationSlugs?.[routeId] ??
                null;
            if (!route) return null;
            const slugMapper = SLUG_MAPPER[routeId];
            if (!slugMapper || !params) return route;
            const newParams = slugMapper(params);
            // Swallow compile errors (e.g. a missing param) so a route lookup
            // can never throw and take down the SSR render.
            try {
                return compile(route, { encode: encodeURIComponent })(
                    newParams
                );
            } catch {
                return null;
            }
        },
        getCurrentRoute: () => ({}),
        getPreviousUrl: () => new URL(''),
        goBack: () => {},
        setURLSearchParams: () => {},
        getPageState: () => ({}),
        pushPageState: () => {},
        replacePageState: () => {},
    } as any,
    wallet: {
        getPlayerCurrency: async () => undefined,
    },
    fetch: fetch.bind(globalThis),
    deviceInfo: {
        deviceId: '',
        deviceHasRegisteredPlayer:
            gateKeeperContext?.deviceHasRegisteredPlayer ?? false,
    },
    isNative: () => ({}) as any,
    cache: {} as any,
    ui: {} as any,
    middleware: [] as any,
    staticContext: { gatewayBaseUrl: gateKeeperContext?.gatewayBaseUrl } as any,
    ...{ featureToggles: gateKeeperContext?.featureToggles ?? {} },
});

export const getGatekeeper = (contextConfig?: TAppContextConfig) =>
    typeof window !== 'undefined' && IS_BROWSER
        ? window?.fabric.getGatekeeper('v2.2.16', mfeConfig.app.name)
        : getSSRGatekeeper(contextConfig?.gatekeeper);

export const gatekeeper = getGatekeeper();
